/**
 * Static Data Context
 * 
 * Provides globally cached static data (categories, families, brands, regions, etc.)
 * that persists across the entire app session.
 * 
 * This context loads all filter options ONCE at app startup and shares them
 * across all components, eliminating redundant API calls.
 * 
 * Benefits:
 * - Single fetch for all static data at app load
 * - Instant access to filter options on any page
 * - No waterfall requests when navigating
 * - Automatic background refresh for stale data
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
    businessAPI,
    productsAPI,
    servicesAPI,
    categoriesAPI,
    platformConfigAPI,
    withCache
} from '../services/api';

/**
 * Extract array data from an API response that might be:
 * - A flat array: ["item1", "item2"]
 * - A paginated object: { count, next, results: [...] }
 * - An object with a data key: { data: [...] }
 */
const extractArray = (response) => {
    if (Array.isArray(response)) return response;
    if (response && typeof response === 'object') {
        if (Array.isArray(response.results)) return response.results;
        if (Array.isArray(response.data)) return response.data;
    }
    return [];
};


const StaticDataContext = createContext(null);

// Cache TTL - static data can be cached longer
const STATIC_DATA_TTL = 30 * 60 * 1000; // 30 minutes

export const StaticDataProvider = ({ children }) => {
    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    // Static data state
    const [staticData, setStaticData] = useState({
        // Business related
        businessCategories: [],
        activeCategoryIds: [],
        coverageRegions: [],
        uniqueBrands: [],
        uniqueCountries: [],

        // Product related
        productCategories: [],
        productFamilies: [],
        productBrands: [],

        // Service related
        serviceCategories: [],
        serviceFamilies: [],
    });

    // Error state
    const [errors, setErrors] = useState({});

    // Track fetch time for refresh logic
    const lastFetchTime = useRef(null);
    const isFetching = useRef(false);

    /**
     * Fetch all static data in parallel
     */
    const fetchAllStaticData = useCallback(async (force = false) => {
        // Prevent duplicate fetches
        if (isFetching.current && !force) {
            return;
        }

        // Check if data is still fresh
        if (!force && lastFetchTime.current &&
            Date.now() - lastFetchTime.current < STATIC_DATA_TTL) {
            return;
        }

        isFetching.current = true;
        setIsLoading(true);

        try {
            // Fetch ALL static data in a single parallel batch
            const results = await Promise.allSettled([
                // Business data
                categoriesAPI.getAll({ page_size: 5000, flat: true }),
                businessAPI.getCoverage(),
                businessAPI.getUniqueBrands(),
                businessAPI.getUniqueCountries(),

                // Product data
                productsAPI.getCategories(),
                platformConfigAPI.getFamilies(), // Dynamic families (v2)
                productsAPI.getBrands(),

                // Service data
                servicesAPI.getCategories(),

                // Active category IDs (categorías con empresas asociadas
                // directa o vía subcategorías). Usado para desactivar
                // visualmente las categorías vacías.
                categoriesAPI.getActiveIds(),
            ]);

            // Process results
            const newData = { ...staticData };
            const newErrors = {};

            // Business Categories
            if (results[0].status === 'fulfilled') {
                const data = extractArray(results[0].value);
                if (data.length > 0) newData.businessCategories = data;
            } else {
                newErrors.businessCategories = results[0].reason?.message || 'Failed to load';
            }

            // Coverage Regions
            if (results[1].status === 'fulfilled') {
                const data = extractArray(results[1].value);
                newData.coverageRegions = data
                    .filter(r => r && typeof r === 'string' && r.trim().length > 0)
                    .map(r => ({ value: r, label: r }));
            } else {
                newErrors.coverageRegions = results[1].reason?.message || 'Failed to load';
            }

            // Unique Brands (businesses)
            if (results[2].status === 'fulfilled') {
                const data = extractArray(results[2].value);
                newData.uniqueBrands = data.map(b => ({ value: b, label: b }));
            } else {
                newErrors.uniqueBrands = results[2].reason?.message || 'Failed to load';
            }

            // Unique Countries
            if (results[3].status === 'fulfilled') {
                const data = extractArray(results[3].value);
                newData.uniqueCountries = data.map(c => ({ value: c, label: c }));
            } else {
                newErrors.uniqueCountries = results[3].reason?.message || 'Failed to load';
            }

            // Helper para mapear el catálogo global de /api/categories/ (results[0])
            // como fallback cuando /products/categories/ y /services/categories/ vienen
            // vacíos (que es el caso en QA y en prod).
            // Preserva level (0=rubro, 1=familia semántica, 2=categoría hoja) y parent (id padre)
            // para que CategoryCascade pueda mostrar la jerarquía Rubro → Familia → Categoría.
            const mapGlobalCategoriesFallback = () => {
                if (results[0].status !== 'fulfilled') return [];
                const fallback = extractArray(results[0].value);
                return fallback
                    .filter(c => c?.is_active !== false && (c?.name || typeof c === 'string'))
                    .map(c => typeof c === 'string'
                        ? { value: c, label: c }
                        : {
                            value: c.slug || c.id?.toString() || c.name,
                            label: c.name || c,
                            id: c.id,
                            level: c.level,
                            parent: c.parent,
                          })
                    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'es'));
            };

            // Product Categories — endpoint legacy /api/products/categories/ está vacío
            // en QA y prod; caemos al catálogo global de /api/categories/ si así viene.
            if (results[4].status === 'fulfilled') {
                const data = extractArray(results[4].value);
                if (data.length > 0) {
                    newData.productCategories = data.map(c => typeof c === 'string'
                        ? { value: c, label: c }
                        : { value: c.slug || c.id?.toString() || c.name, label: c.name || c, id: c.id, level: c.level, parent: c.parent });
                } else {
                    newData.productCategories = mapGlobalCategoriesFallback();
                }
            } else {
                newErrors.productCategories = results[4].reason?.message || 'Failed to load';
                newData.productCategories = mapGlobalCategoriesFallback();
            }

            // Dynamic Families (Product & Service)
            if (results[5].status === 'fulfilled') {
                const data = extractArray(results[5].value);

                // Process Product Families
                newData.productFamilies = data
                    .filter(f => f.entity_type === 'product')
                    .map(f => ({ value: f.code, label: f.name }));

                // Process Service Families
                newData.serviceFamilies = data
                    .filter(f => f.entity_type === 'service')
                    .map(f => ({ value: f.code, label: f.name }));
            } else {
                newErrors.families = results[5].reason?.message || 'Failed to load';
            }

            // Product Brands
            if (results[6].status === 'fulfilled') {
                const data = extractArray(results[6].value);
                newData.productBrands = data.map(b => ({ value: b, label: b }));
            } else {
                newErrors.productBrands = results[6].reason?.message || 'Failed to load';
            }

            // Service Categories — mismo fallback al catálogo global.
            if (results[7].status === 'fulfilled') {
                const data = extractArray(results[7].value);
                if (data.length > 0) {
                    newData.serviceCategories = data.map(c => typeof c === 'string'
                        ? { value: c, label: c }
                        : { value: c.slug || c.id?.toString() || c.name, label: c.name || c, id: c.id, level: c.level, parent: c.parent });
                } else {
                    newData.serviceCategories = mapGlobalCategoriesFallback();
                }
            } else {
                newErrors.serviceCategories = results[7].reason?.message || 'Failed to load';
                newData.serviceCategories = mapGlobalCategoriesFallback();
            }


                        // Active Category IDs
            if (results[8].status === 'fulfilled') {
                const payload = results[8].value;
                const arr = Array.isArray(payload?.ids) ? payload.ids : [];
                newData.activeCategoryIds = arr;
            } else {
                newErrors.activeCategoryIds = results[8].reason?.message || 'Failed to load';
            }

            setStaticData(newData);
            setErrors(newErrors);
            lastFetchTime.current = Date.now();
            setIsInitialized(true);

            if (import.meta.env.DEV) {
                console.log('📦 Static data loaded:', {
                    businessCategories: newData.businessCategories.length,
                    coverageRegions: newData.coverageRegions.length,
                    uniqueBrands: newData.uniqueBrands.length,
                    uniqueCountries: newData.uniqueCountries.length,
                    productCategories: newData.productCategories.length,
                    productFamilies: newData.productFamilies.length,
                    productBrands: newData.productBrands.length,
                    serviceCategories: newData.serviceCategories.length,
                });
            }
        } catch (error) {
            console.error('Error fetching static data:', error);
        } finally {
            setIsLoading(false);
            isFetching.current = false;
        }
    }, [staticData]);

    // Initial fetch on mount
    useEffect(() => {
        fetchAllStaticData();
    }, []);

    // Background refresh when app regains focus (if data is stale)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' &&
                lastFetchTime.current &&
                Date.now() - lastFetchTime.current > STATIC_DATA_TTL) {
                fetchAllStaticData(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchAllStaticData]);

    /**
     * Get formatted category options for dropdowns
     */
    const getBusinessCategoryOptions = useCallback(() => {
        return staticData.businessCategories.map(cat => ({
            value: cat.slug || cat.id?.toString(),
            label: cat.name
        }));
    }, [staticData.businessCategories]);

    /**
     * Extract search suggestions from category descriptions (dynamic, from Django)
     * Categories with parent have terms stored as "term1 | term2 | term3" in description
     */

    /**
     * Refresh all static data
     */
    const refresh = useCallback(() => {
        return fetchAllStaticData(true);
    }, [fetchAllStaticData]);

    /**
     * Get descriptive label for a family code
     */
    const getFamilyLabel = useCallback((value, type = 'product') => {
        if (!value) return '-';
        const list = type === 'product' ? staticData.productFamilies : staticData.serviceFamilies;
        const found = list.find(f => f.value === value);
        return found ? found.label : value;
    }, [staticData.productFamilies, staticData.serviceFamilies]);

    const value = {
        // Data
        ...staticData,

        // Formatted for dropdowns
        businessCategoryOptions: getBusinessCategoryOptions(),

        // Search suggestions from taxonomy (dynamic)

        // State
        isLoading,
        isInitialized,
        errors,

        // Actions
        refresh,
        getFamilyLabel,
    };

    return (
        <StaticDataContext.Provider value={value}>
            {children}
        </StaticDataContext.Provider>
    );
};

/**
 * Hook to access static data
 */
export const useStaticData = () => {
    const context = useContext(StaticDataContext);
    if (!context) {
        throw new Error('useStaticData must be used within a StaticDataProvider');
    }
    return context;
};

export default StaticDataContext;
